import { ScoreDataQuery } from '../ScoreData.generated';

export interface ScoreData {
	calculation_date: string;
	unit_id: string | null;
	environment: string | null;
	score: number | null;
	timeframe_days: number | null;
}

export interface ScoreDataResult {
	scores: ScoreData[];
	lastUpdatedAt: number;
}

type RawScoreRecord = NonNullable<NonNullable<ScoreDataQuery['score_web_products']>[number]>;

export const transformScoreDataResults = (rawResults: ScoreDataQuery): ScoreDataResult => {
	const rawScores =
		rawResults?.score_web_products?.filter((score): score is RawScoreRecord => score !== null) ??
		[];

	// Group by calculation_date so we can weighted-average across environments per day
	const dateGroups = new Map<string, RawScoreRecord[]>();
	rawScores.forEach((score) => {
		const dateKey = score.calculation_date ?? '';
		const group = dateGroups.get(dateKey) ?? [];
		group.push(score);
		dateGroups.set(dateKey, group);
	});

	const scores: ScoreData[] = Array.from(dateGroups.entries())
		.map(([dateKey, group]) => {
			const totalUniqueIssues = group.reduce((sum, s) => sum + (s.unique_issues ?? 0), 0);
			const weightedNumerator = group.reduce(
				(sum, s) => sum + (s.score ?? 0) * (s.unique_issues ?? 0),
				0
			);
			// Fall back to simple average when unique_issues are all zero
			const weightedRaw =
				totalUniqueIssues > 0
					? weightedNumerator / totalUniqueIssues
					: group.reduce((sum, s) => sum + (s.score ?? 0), 0) / (group.length || 1);

			return {
				calculation_date: dateKey,
				unit_id: group[0]?.unit_id ?? null,
				environment: null,
				score: Math.round(weightedRaw * 100),
				timeframe_days: group[0]?.timeframe_days ?? null
			};
		})
		.sort(
			(a, b) => new Date(a.calculation_date).getTime() - new Date(b.calculation_date).getTime()
		);

	const lastUpdatedAt = rawScores.reduce((max, score) => {
		if (!score.created_at) {
			return max;
		}
		const timestamp = new Date(score.created_at).getTime();
		return timestamp > max ? timestamp : max;
	}, 0);

	return {
		scores,
		lastUpdatedAt
	};
};
